import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
} from '@angular/core';
import { SearchSubmitMethod } from '@app/search/services/search-tracker.service';
import { SimpleItemViewModel } from '@app/shared/models/core-view.model';
import { AuthService } from '@app/shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { FollowingService } from '@app/shared/services/following.service';
import { ProximitySearchResults } from '@app/user/user-api.model';
import { catchError } from 'rxjs/operators';
import { getDeepCopy } from '@app/shared/utils/property';
import { of } from 'rxjs';

export interface PeopleMatchViewModel
  extends SimpleItemViewModel<ProximitySearchResults> {}

@Component({
  selector: 'dgx-search-people',
  templateUrl: './search-people.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchPeopleComponent {
  @Input() public items: PeopleMatchViewModel[];
  @Input() public isLoading: boolean;
  @Input() public totalNumItems: number;
  @Input() public searchTerm: string;

  public i18n = this.translateService.instant([
    'LearningSearch_People',
    'Core_Follow',
    'Core_UnFollow',
  ]);

  constructor(
    private followingService: FollowingService,
    private authService: AuthService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  public get linkData() {
    return {
      route: '/learning/people',
      searchTerm: this.searchTerm,
      collectionLink: SearchSubmitMethod.morePeople,
    };
  }

  public get showNoResults(): boolean {
    return this.totalNumItems === 0 && !this.isLoading;
  }

  public get showError(): boolean {
    return !this.items && !this.isLoading;
  }

  public getButtonIcon(isFollowing: boolean): string {
    return isFollowing ? 'checkmark' : 'plus';
  }

  public getButtonAriaLabel(isFollowing: boolean, username: string): string {
    return isFollowing
      ? this.translateService.instant('dgUsersSearch_UnfollowUserFormat', {
          username,
        })
      : this.translateService.instant('dgUsersSearch_FollowUserFormat', {
          username,
        });
  }

  public getButtonType(isFollowing: boolean) {
    return isFollowing ? 'primary' : 'passive';
  }

  public getIsAuthorizedUser(userProfileKey: number): boolean {
    return (
      this.authService.authUser?.viewerProfile.userProfileKey === userProfileKey
    );
  }

  public doFollowAction(
    item: SimpleItemViewModel<ProximitySearchResults>
  ): void {
    // Cache item so we can easily revert on errors
    const cachedItem = getDeepCopy(item);
    const isFollowing = cachedItem.model.isFollowing;
    const action = isFollowing ? 'unfollowUser' : 'followUser';
    const explainText = isFollowing
      ? this.i18n.Core_Follow
      : this.i18n.Core_UnFollow;

    // Immediately update UI for better user experience
    this.updateItem(item.model.userProfileKey, !isFollowing, explainText);

    this.followingService[action](
      item.model.userProfileId,
      item.model.name,
      item.model.isEngaged
    )
      .pipe(
        catchError((error) => {
          // Revert changes if request fails
          this.updateItem(
            cachedItem.model.userProfileKey,
            cachedItem.model.isFollowing,
            cachedItem.explainText
          );
          this.cdr.markForCheck();
          return of(error);
        })
      )
      .subscribe();
  }

  public trackByFn(
    index: number,
    item: SimpleItemViewModel<ProximitySearchResults>
  ): number {
    return item.model.userProfileKey;
  }

  private updateItem(
    userProfileKey: number,
    isFollowing: boolean,
    explainText: string
  ): void {
    this.items = this.items.map((item) => {
      if (item.model.userProfileKey === userProfileKey) {
        return {
          ...item,
          explainText,
          model: {
            ...item.model,
            isFollowing,
          },
        };
      }
      return item;
    });
  }
}
